import type { Passage } from "./types";

export const passage: Passage = {
  slug: "john-3-16",
  reference: "John 3:16",
  title: "John 3:16 meaning, and the word everyone reads wrong",
  h1: 'John 3:16, and what "so loved" actually says',
  description:
    "The best-known verse in English is usually read as a measure of how much. The Greek says how — and the sentence before it explains what the manner was.",
  targets: [
    "john 3 16 meaning",
    "what does john 3 16 mean",
    "for god so loved the world explained",
  ],
  published: "2026-09-03",
  standfirst:
    "It has been on placards at football matches for fifty years, which is roughly how long the most important word in it has been misread.",
  verse: {
    text: "For God so loved the world, that he gave his only born Son, that whoever believes in him should not perish, but have eternal life.",
    ref: "John 3:16",
  },
  context: [
    {
      text: "As Moses lifted up the serpent in the wilderness, even so must the Son of Man be lifted up, that whoever believes in him should not perish, but have eternal life.",
      ref: "John 3:14–15",
    },
    {
      text: "For God didn't send his Son into the world to judge the world, but that the world should be saved through him.",
      ref: "John 3:17",
    },
  ],
  takeaways: [
    "So in this verse is manner, not quantity. The Greek houtos means in this way — and the way is described in the verse immediately before it.",
    "The preceding image is Moses lifting a bronze serpent on a pole in the wilderness, which is a strange and unflattering comparison and is the one Jesus chose.",
    "Verse 17 is part of the same sentence in effect, and it is the one that gets left off the placard: he did not come to judge.",
    "None of this reduces the verse. It makes it specific — a statement about what was done rather than about how strong the feeling was.",
  ],
  body: [
    {
      type: "h2",
      id: "the-word-so",
      text: 'The word "so"',
    },
    {
      type: "p",
      text: "In modern English, so loved reads as an intensifier — loved so much. The Greek word is houtos, which means in this manner, like this, thus. It answers how rather than how much, and it points backwards to something already described.",
    },
    {
      type: "p",
      text: "Several recent translations make this explicit: God loved the world in this way. It is not a downgrade of the verse. It is a redirection of it — from a claim about the size of an emotion to a claim about a specific act.",
    },
    {
      type: "h2",
      id: "what-comes-before",
      text: "What comes immediately before",
    },
    {
      type: "p",
      text: "Verses 14 and 15 are the antecedent. Jesus compares himself to a bronze serpent Moses put on a pole in the wilderness, which the Israelites looked at to survive a plague of snakes. It is an odd, faintly grotesque image, and it is the one being pointed at by the word so.",
    },
    {
      type: "p",
      text: "So the sense is: in the manner of that thing lifted up, God loved the world. The love is defined by an execution rather than by an adjective, which is why the verse's original setting is a night conversation with a nervous religious official rather than a stadium.",
    },
    {
      type: "h2",
      id: "the-verse-after",
      text: "The verse after it",
    },
    {
      type: "p",
      text: "Verse 17 says he did not send the Son to judge the world. It rarely appears alongside 16, which is a shame, because the pairing is what stops the famous verse being heard as a threat with a way out attached.",
    },
    {
      type: "p",
      text: "Read together, the two verses say the same thing twice: the purpose was rescue. The judgement language that follows in verses 18 to 21 is about people preferring darkness, which is a different claim from God arriving to prosecute.",
    },
  ],
  misreading: {
    claim:
      "That the verse measures the intensity of God's love — God loved the world SO much that he did this — with the emphasis falling on the size of the feeling.",
    correction: [
      "This is the reading nearly everybody has, and it is not heretical: the New Testament says elsewhere, at length, that the love is vast and unmeasured. But it is not what this particular sentence says, and something specific is lost in the swap.",
      "Houtos is a manner word. In this way. The way is the bronze serpent of verse 14 — something lifted up in a wilderness for people to look at and live. That comparison is deliberately unglamorous, and the verse defines love as that act rather than as a quantity.",
      "The practical difference is what happens when the feeling is not available. A verse about how much God loves you is hard to hold on a day you feel nothing. A verse about something that was done, at a particular time, in a particular manner, is not affected by how you feel about it.",
      "There is a second loss in the popular reading, and it is about the word world. Made a statement of intensity, the sentence's centre of gravity moves to the emotion and the object of it becomes background. Made a statement of manner, the object stays in view — the world, which in John's vocabulary is not a compliment. It is the system that does not recognise him, described three chapters later as hating him. That is what is being loved in this way.",
    ],
  },
  use: [
    {
      type: "h2",
      id: "how-to-use-it",
      text: "How to use it",
    },
    {
      type: "p",
      text: "Read verses 14 to 17 together rather than 16 alone. It takes twenty seconds and it supplies both the antecedent for so and the sentence about not coming to judge.",
    },
    {
      type: "p",
      text: "And notice where the conversation is happening: at night, with a man who came in the dark because he did not want to be seen. That setting does more for the verse than any amount of underlining — the same way {Jeremiah-29-11} changes when you find out who it was written to.",
      links: { "Jeremiah-29-11": "/verse/jeremiah-29-11" },
    },
    {
      type: "note",
      text: "It is also worth knowing where the famous sentence stops. Most modern translations close the quotation marks somewhere around verse 15 or 21, because Greek has no quotation marks and it is genuinely unclear whether verses 16 to 21 are Jesus speaking or the gospel writer commenting. Nothing in the meaning turns on it, but it explains why the verse reads slightly like a summary: it may be one.",
    },
    {
      type: "note",
      text: "If the verse has gone flat from repetition, the fastest way back is to read it in the paragraph. It is a different sentence there.",
    },
  ],
  faq: [
    {
      q: "What does so mean in John 3:16?",
      a: "It translates the Greek houtos, which means in this manner or in this way rather than so much. It points back to verse 14, where Jesus compares himself to the bronze serpent Moses lifted up in the wilderness.",
    },
    {
      q: "What does John 3:16 actually mean?",
      a: "That God loved the world in a specific way — by giving the Son to be lifted up, so that anyone who trusts him has life rather than perishing. Verse 17 adds that the purpose was rescue rather than judgement.",
    },
    {
      q: "Why is John 3:16 the most famous verse?",
      a: "It summarises the gospel in one sentence and is short enough to memorise. Notably, it has slipped in the most-read rankings — Bible Gateway had it at 24th in 2025, behind several verses of Psalm 23 and Psalm 91.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so the verse is printed whole rather than referred to. It reads 'his only born Son' where the King James has 'only begotten' and newer translations have 'one and only'; the sentence is otherwise the familiar one.",
    },
  ],
  related: ["how-to-memorize-bible-verses", "bible-in-a-year-on-your-phone"],
  relatedTopics: ["bible-verses-about-forgiveness"],
  relatedPassages: ["jeremiah-29-11", "romans-12-2"],
};
